import { z } from 'zod';

/**
 * Подмножество ProseMirror JSON, которое используется в rich text объектов доски.
 * Совпадает со схемой Tiptap (StarterKit + Underline + Link + TextStyle/Color +
 * Highlight + TextAlign), поэтому JSON можно напрямую отдавать в редактор,
 * а в Yjs он хранится как Y.XmlFragment (y-prosemirror).
 */

export const MARK_TYPES = ['bold', 'italic', 'underline', 'strike', 'code', 'link', 'textStyle', 'highlight'] as const;
export type MarkType = (typeof MARK_TYPES)[number];

export interface RichMark {
  type: MarkType;
  attrs?: Record<string, unknown>;
}

export interface RichInline {
  type: 'text' | 'hardBreak';
  text?: string;
  marks?: RichMark[];
}

export interface RichBlock {
  type: 'paragraph' | 'heading' | 'bulletList' | 'orderedList' | 'listItem' | 'taskList' | 'taskItem';
  attrs?: Record<string, unknown>;
  content?: Array<RichBlock | RichInline>;
}

export interface RichDoc {
  type: 'doc';
  content: RichBlock[];
}

const markSchema = z.object({
  type: z.enum(MARK_TYPES),
  attrs: z.record(z.string(), z.unknown()).optional(),
});

const inlineSchema = z.object({
  type: z.enum(['text', 'hardBreak']),
  text: z.string().max(20_000).optional(),
  marks: z.array(markSchema).max(16).optional(),
});

export const richBlockSchema: z.ZodType<RichBlock> = z.lazy(() =>
  z.object({
    type: z.enum(['paragraph', 'heading', 'bulletList', 'orderedList', 'listItem', 'taskList', 'taskItem']),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(z.union([richBlockSchema, inlineSchema])).max(2_000).optional(),
  }),
) as z.ZodType<RichBlock>;

export const richDocSchema: z.ZodType<RichDoc> = z.object({
  type: z.literal('doc'),
  content: z.array(richBlockSchema).max(2_000),
}) as z.ZodType<RichDoc>;

export function emptyDoc(): RichDoc {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

/** Простой текст → документ: каждая строка — абзац. */
export function plainTextToDoc(text: string, align?: 'left' | 'center' | 'right'): RichDoc {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  return {
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph' as const,
      ...(align ? { attrs: { textAlign: align } } : {}),
      ...(line ? { content: [{ type: 'text' as const, text: line }] } : {}),
    })),
  };
}

/** Документ → простой текст (для поиска, экспорта, API). */
export function docToPlainText(doc: RichDoc | null | undefined): string {
  if (!doc) return '';
  const out: string[] = [];
  const walkBlock = (node: RichBlock | RichInline, prefix = ''): void => {
    if (node.type === 'text') {
      out.push((node as RichInline).text ?? '');
      return;
    }
    if (node.type === 'hardBreak') {
      out.push('\n');
      return;
    }
    const block = node as RichBlock;
    if (block.type === 'bulletList' || block.type === 'orderedList' || block.type === 'taskList') {
      let i = 1;
      for (const item of block.content ?? []) {
        const p = block.type === 'orderedList' ? `${i++}. ` : '• ';
        walkBlock(item, p);
      }
      return;
    }
    if (block.type === 'listItem' || block.type === 'taskItem') {
      let first = true;
      for (const child of block.content ?? []) {
        walkBlock(child, first ? prefix : '  ');
        first = false;
      }
      return;
    }
    if (prefix) out.push(prefix);
    for (const child of block.content ?? []) walkBlock(child);
    out.push('\n');
  };
  for (const b of doc.content) walkBlock(b);
  return out.join('').replace(/\n+$/, '');
}

/** Документ пуст (нет ни одного видимого символа). */
export function isDocEmpty(doc: RichDoc | null | undefined): boolean {
  return docToPlainText(doc).trim().length === 0;
}
